import "./App.css";

import React, { useState, useRef } from "react";
import { MapContainer, FeatureGroup } from 'react-leaflet'
import 'leaflet/dist/leaflet.css';
import CanopyLayers from "./CanopyLayers.js";
import { CanopyButtons, AdditionalButtons } from "./CanopyButtons.js";
import { CutawayInputs } from "./CutawayInputs.js";
import { CanopyReleaseLocationAlert, ReleaseLocationNotSetAlert } from "./Alerts.js";
import CustomGeoJSON from "./CustomGeoJSON.js";
import MapClickHandler from "./MapClickHandler.js";
import { DrawControls } from "./DrawControls.js";
import { Onboarding } from "./Onboarding.js";
import { DrawnLayers } from "./DrawnLayers.js";
import { TrackLayers } from "./TrackLayers.tsx";
import { LocationIndicator } from "./LocationIndicator.tsx";
import { RoomDialog } from "./RoomDialog.js";
import { UsernameDialog } from "./UsernameDialog.tsx";
import { AdminDialog } from "./AdminDialog.tsx";
import { LocationConsentDialog } from "./LocationConsentDialog.tsx";
import './i18n/config';
import { useTranslation } from 'react-i18next';

// Custom hooks
import { useWebSocket } from "./hooks/useWebSocket";
import { useLocationTracking } from "./hooks/useLocationTracking";
import { useUsername } from "./hooks/useUsername";
import { useDialogState } from "./hooks/useDialogState";
import { useCanopyPrediction } from "./hooks/useCanopyPrediction";
import { useDrawnLayers } from "./hooks/useDrawnLayers";
import { useTrackData } from "./hooks/useTrackData";



interface CurrentLocationData {
  latitude: number;
  longitude: number;
  accuracy: number;
  heading?: number;
  timestamp: number;
}

function App() {
  const mapRef = useRef(null);
  const [selectedLayer, setSelectedLayer] = useState('OSM');
  const [gpsTestingActive, setGpsTestingActive] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<CurrentLocationData | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [additionalMobileMenuOpen, setAdditionalMobileMenuOpen] = useState(false);
  const { t } = useTranslation();

  // Initialize dialog state first (no dependencies)
  const dialogState = useDialogState();
  
  // Initialize username hook with stable initial username from localStorage
  const [initialUsername] = useState(() => {
    const saved = localStorage.getItem('username');
    if (saved) return saved;
    const generated = `User_${Math.floor(Math.random() * 10000)}`;
    localStorage.setItem('username', generated);
    return generated;
  });
  
  // Create refs for the handlers to avoid circular dependencies
  const handlersRef = useRef({
    onLayerUpdate: (layer: any) => {},
    onLayerDelete: (layerId: string) => {},
    onCanopyPrediction: (message: any) => {},
    onUserList: (users: Array<{ id: string; username: string }>) => {},
    onUsernameResponse: (userId: string, username: string, requestId: string) => {},
    onAdminStatusResponse: (isAdmin: boolean) => {},
    onTracksResponse: (tracks: any[]) => {},
    onTrackPointsResponse: (trackId: string, points: any[]) => {},
    onUserLocationUpdate: (message: any) => {}
  });

  // Initialize WebSocket first
  const webSocketHook = useWebSocket(handlersRef, initialUsername);

  // Now initialize other hooks with the sendMessage function
  const usernameHook = useUsername({ sendMessage: webSocketHook.sendMessage });
  const drawnLayersHook = useDrawnLayers({ sendMessage: webSocketHook.sendMessage });
  const canopyPredictionHook = useCanopyPrediction({ sendMessage: webSocketHook.sendMessage });
  const trackDataHook = useTrackData({ sendMessage: webSocketHook.sendMessage });
  const locationTrackingHook = useLocationTracking({
    sendMessage: webSocketHook.sendMessage,
    onShowLocationConsent: dialogState.showLocationConsent,
    onLocationUpdate: (location: CurrentLocationData) => {
      // Update current location for real-time indicator
      setCurrentLocation(location);
    }
  });

  // Update the handlers once all hooks are initialized
  React.useEffect(() => {
    handlersRef.current = {
      onLayerUpdate: drawnLayersHook.handleLayerUpdate,
      onLayerDelete: drawnLayersHook.handleLayerDelete,
      onCanopyPrediction: canopyPredictionHook.handleCanopyPredictionMessage,
      onUserList: usernameHook.handleUserList,
      onUsernameResponse: usernameHook.handleUsernameResponse,
      onAdminStatusResponse: usernameHook.handleAdminStatusResponse,
      onTracksResponse: trackDataHook.handleTracksResponse,
      onTrackPointsResponse: trackDataHook.handleTrackPointsResponse,
      onUserLocationUpdate: trackDataHook.handleUserLocationUpdate
    };
  }, [
    drawnLayersHook.handleLayerUpdate,
    drawnLayersHook.handleLayerDelete,
    canopyPredictionHook.handleCanopyPredictionMessage,
    usernameHook.handleUserList,
    usernameHook.handleUsernameResponse,
    usernameHook.handleAdminStatusResponse,
    trackDataHook.handleTracksResponse,
    trackDataHook.handleTrackPointsResponse,
    trackDataHook.handleUserLocationUpdate
  ]);

  // Clear current location when location tracking stops
  React.useEffect(() => {
    if (!locationTrackingHook.locationWatching) {
      setCurrentLocation(null);
    }
  }, [locationTrackingHook.locationWatching]);

  // Clean room parameter from URL after initial load
  React.useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has('room')) {
      // Remove the room parameter from URL without reloading
      url.searchParams.delete('room');
      window.history.replaceState({}, '', url.toString());
    }
  }, []); // Run only once on mount

  // Event handlers
  const onShowEditControls = (show: boolean) => {
    if (show) dialogState.openDialog('editControls');
    else dialogState.closeDialog('editControls');
  };

  const onShowCanopySearch = () => {
    dialogState.toggleDialog('canopySidePanel');
  };

  const newCanopyReleaseLocationSet = (latLng: any) => {
    canopyPredictionHook.handleNewReleaseLocationSet(latLng);
    dialogState.canopyCutawayLocationSet();
  };

  // GPS testing handlers
  const handleGpsTestingChange = (active: boolean) => {
    setGpsTestingActive(active);
  };

  const handleGpsTestClick = (lat: number, lng: number) => {
    if (gpsTestingActive) {
      // Send fake GPS location using the same message format as real tracking
      webSocketHook.sendMessage({
        type: 'update_user_location',
        latitude: lat,
        longitude: lng,
        timestamp: Date.now()
      });
    }
  };


  return (
    <>
      <CutawayInputs
        open={dialogState.dialogs.canopySidePanel}
        onClose={() => dialogState.closeDialog('canopySidePanel')}
        onSetCutawayLocation={dialogState.setCanopyCutawayLocation}
        releaseLocation={canopyPredictionHook.releaseLocation}
        cutawayParameters={canopyPredictionHook.cutawayParameters}
        onParametersChanged={canopyPredictionHook.handleParametersChanged}
      />
      
      <CanopyReleaseLocationAlert show={dialogState.settingCanopyReleaseLocation} />
      
      <ReleaseLocationNotSetAlert 
        show={!canopyPredictionHook.cutawayParameters.releaseLocation && !dialogState.settingCanopyReleaseLocation} 
        onShowCanopySearch={onShowCanopySearch} 
        onSetCutawayLocation={dialogState.setCanopyCutawayLocation}
      />
      
      <RoomDialog 
        open={dialogState.dialogs.room} 
        onClose={() => dialogState.closeDialog('room')}
      />
      
      <UsernameDialog
        open={dialogState.dialogs.username}
        onClose={() => dialogState.closeDialog('username')}
        currentUsername={usernameHook.username}
        onUsernameChange={usernameHook.handleUsernameChange}
      />
      
      <AdminDialog
        open={dialogState.showAdminDialog}
        onClose={() => dialogState.setShowAdminDialog(false)}
        socketRef={webSocketHook.socketRef}
        sendMessage={webSocketHook.sendMessage}
        onGpsTestingChange={handleGpsTestingChange}
        onGpsTestClick={handleGpsTestClick}
      />
      
      <LocationConsentDialog
        open={dialogState.dialogs.locationConsent}
        onClose={() => dialogState.closeDialog('locationConsent')}
        onAccept={dialogState.handleLocationConsentAccept}
      />

      <MapContainer style={{ height: '100vh', width: '100vw' }} center={[61.779, 22.714]} zoom={15} zoomControl={false} ref={mapRef} id='map-container'>
        <MapClickHandler 
          relayClickLocations={dialogState.settingCanopyReleaseLocation} 
          onMapClick={newCanopyReleaseLocationSet}
          gpsTestingActive={gpsTestingActive}
          onGpsTestClick={handleGpsTestClick}
        />
        
        <CanopyLayers selectedLayer={selectedLayer} />
        
        <CanopyButtons 
          showColors={dialogState.dialogs.editControls} 
          key="canopy-buttons" 
          isAdmin={usernameHook.isAdmin} 
          onStartTutorial={dialogState.startTutorial} 
          onShowEditControls={() => onShowEditControls(!dialogState.dialogs.editControls)} 
          onShowCanopySearch={onShowCanopySearch} 
          onShowRoom={() => dialogState.openDialog('room')} 
          onShowUsername={() => dialogState.openDialog('username')} 
          onShowAdmin={() => dialogState.setShowAdminDialog(true)} 
          showEditControlsDefault={dialogState.dialogs.editControls} 
          onSelectColor={drawnLayersHook.setDrawColor}
          mobileMenuOpen={mobileMenuOpen}
          setMobileMenuOpen={setMobileMenuOpen}
        />
        
        <DrawControls 
          show={dialogState.dialogs.editControls} 
          hexColor={drawnLayersHook.drawColor} 
          onLayerEdited={drawnLayersHook.onLayerEdited} 
          onLayerDeleted={drawnLayersHook.onLayerDeleted}
        />
        
        <DrawnLayers 
          geojson={drawnLayersHook.drawnLayers} 
          socketRef={webSocketHook.socketRef} 
          usernameCache={usernameHook.usernameCache} 
          onRegisterPendingRequest={usernameHook.registerPendingRequest}
          onLayerEdited={drawnLayersHook.onLayerEdited}
          onLayerDeleted={drawnLayersHook.onLayerDeleted}
        />
        
        <TrackLayers 
          geojson={trackDataHook.tracksGeoJSON} 
          socketRef={webSocketHook.socketRef} 
          usernameCache={usernameHook.usernameCache} 
          onRegisterPendingRequest={usernameHook.registerPendingRequest} 
        />
        
        {/* Real-time location indicator (only visible when tracking) */}
        {currentLocation && locationTrackingHook.locationWatching && (
          <LocationIndicator
            latitude={currentLocation.latitude}
            longitude={currentLocation.longitude}
            accuracy={currentLocation.accuracy}
            heading={currentLocation.heading}
            timestamp={currentLocation.timestamp}
          />
        )}
        
        <FeatureGroup>
          <CustomGeoJSON 
            geojson={canopyPredictionHook.predictionGeojson} 
            socketRef={webSocketHook.socketRef} 
            usernameCache={usernameHook.usernameCache} 
            onRegisterPendingRequest={usernameHook.registerPendingRequest} 
            pathOptions={{ pmIgnore: true }}
          />
        </FeatureGroup>
        
        <AdditionalButtons 
          releaseLocation={canopyPredictionHook.cutawayParameters.releaseLocation} 
          onSetCutawayLocation={dialogState.setCanopyCutawayLocation} 
          selectedLayer={selectedLayer} 
          onLayerChange={setSelectedLayer}
          locationWatching={locationTrackingHook.locationWatching}
          onLocationTrackingToggle={locationTrackingHook.handleLocationTrackingToggle}
          mobileMenuOpen={additionalMobileMenuOpen}
          setMobileMenuOpen={setAdditionalMobileMenuOpen}
        />
        
        <Onboarding 
          startOnBoarding={dialogState.onboardingRun} 
          onOnboardingStarted={dialogState.handleOnboardingStarted} 
          setShowCanopySidePanel={() => dialogState.openDialog('canopySidePanel')}
          setShowMobileMenu={setMobileMenuOpen}
          setShowAdditionalMobileMenu={setAdditionalMobileMenuOpen}
        />
      </MapContainer>
    </>
  );
}

export default App;
